import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
    Field,
    ChipsField,
    GeneratorConfigV1,
    SliderField,
    NumberField,
    TextareaField,
    FieldOption,
    PricingImpact,
    VisibilityRule,
} from '../../types/generator-config';
import { labelForType, iconForType, fieldHasOptions } from '../../lib/fieldFactory';
import { OptionsEditor } from './OptionsEditor';
import { CollapsibleBlock } from './CollapsibleBlock';
import { ConditionBuilder } from './ConditionBuilder';
import { ImpactEditor } from './ImpactEditor';
import { Icon } from '../Icon';

interface FieldEditorProps {
    field: Field;
    config: GeneratorConfigV1;
    onChange: (next: Field) => void;
    onRemove: () => void;
    onDuplicate: () => void;
}

export function FieldEditor({ field, config, onChange, onRemove, onDuplicate }: FieldEditorProps) {
    const [expanded, setExpanded] = useState(false);

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: field.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const setBase = <K extends keyof Field>(key: K, value: Field[K]) => {
        onChange({ ...field, [key]: value } as Field);
    };

    const setOptions = (options: FieldOption[]) => {
        if (fieldHasOptions(field)) {
            onChange({ ...field, options } as Field);
        }
    };

    return (
        <li ref={setNodeRef} style={style} className={`field-card${expanded ? ' is-expanded' : ''}`}>
            <header className="field-card-head">
                <button
                    type="button"
                    className="drag-handle"
                    aria-label="Réordonner le champ"
                    {...attributes}
                    {...listeners}
                >
                    <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden>
                        <circle cx="2" cy="2" r="1.2" /><circle cx="8" cy="2" r="1.2" />
                        <circle cx="2" cy="7" r="1.2" /><circle cx="8" cy="7" r="1.2" />
                        <circle cx="2" cy="12" r="1.2" /><circle cx="8" cy="12" r="1.2" />
                    </svg>
                </button>

                <button
                    type="button"
                    className="field-card-title"
                    onClick={() => setExpanded((v) => !v)}
                >
                    <span className="field-card-icon" aria-hidden>
                        <Icon name={iconForType(field.type)} size={16} />
                    </span>
                    <span className="field-card-label">{field.label || <em>Sans titre</em>}</span>
                    <span className="field-card-type">{labelForType(field.type)}</span>
                    <svg
                        width="14" height="14" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round"
                        className="field-card-chevron"
                        aria-hidden
                    >
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                <div className="field-card-actions">
                    <button
                        type="button"
                        className="icon-btn"
                        onClick={onDuplicate}
                        title="Dupliquer"
                        aria-label="Dupliquer"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className="icon-btn icon-btn-danger"
                        onClick={onRemove}
                        title="Supprimer"
                        aria-label="Supprimer"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        </svg>
                    </button>
                </div>
            </header>

            {expanded && (
                <div className="field-card-body">
                    <label className="field">
                        <span className="field-label">Libellé affiché</span>
                        <input
                            type="text"
                            value={field.label}
                            onChange={(e) => setBase('label', e.target.value)}
                            placeholder="Ex : Quel type de projet ?"
                        />
                    </label>

                    <label className="field">
                        <span className="field-label">Aide / description (optionnel)</span>
                        <input
                            type="text"
                            value={field.helper ?? ''}
                            onChange={(e) =>
                                setBase('helper', e.target.value || undefined)
                            }
                            placeholder="Petit texte d'aide affiché sous le champ"
                        />
                    </label>

                    <label className="field field-checkbox">
                        <input
                            type="checkbox"
                            checked={!!field.required}
                            onChange={(e) => setBase('required', e.target.checked)}
                        />
                        <span>Champ obligatoire</span>
                    </label>

                    {/* Options spécifiques au type */}
                    {field.type === 'chips' && (
                        <label className="field field-checkbox">
                            <input
                                type="checkbox"
                                checked={(field as ChipsField).multi}
                                onChange={(e) =>
                                    onChange({
                                        ...(field as ChipsField),
                                        multi: e.target.checked,
                                    })
                                }
                            />
                            <span>Autoriser plusieurs sélections</span>
                        </label>
                    )}

                    {(field.type === 'slider' || field.type === 'number') && (
                        <div className="fields-row-3">
                            <label className="field">
                                <span className="field-label">Min</span>
                                <input
                                    type="number"
                                    value={(field as SliderField | NumberField).min ?? 0}
                                    onChange={(e) =>
                                        onChange({
                                            ...(field as SliderField | NumberField),
                                            min: Number(e.target.value),
                                        })
                                    }
                                />
                            </label>
                            <label className="field">
                                <span className="field-label">Max</span>
                                <input
                                    type="number"
                                    value={(field as SliderField | NumberField).max ?? 100}
                                    onChange={(e) =>
                                        onChange({
                                            ...(field as SliderField | NumberField),
                                            max: Number(e.target.value),
                                        })
                                    }
                                />
                            </label>
                            <label className="field">
                                <span className="field-label">Pas</span>
                                <input
                                    type="number"
                                    value={(field as SliderField | NumberField).step ?? 1}
                                    onChange={(e) =>
                                        onChange({
                                            ...(field as SliderField | NumberField),
                                            step: Number(e.target.value),
                                        })
                                    }
                                />
                            </label>
                        </div>
                    )}

                    {(field.type === 'slider' || field.type === 'number') && (
                        <label className="field">
                            <span className="field-label">Unité (optionnel)</span>
                            <input
                                type="text"
                                value={(field as SliderField | NumberField).unit ?? ''}
                                onChange={(e) =>
                                    onChange({
                                        ...(field as SliderField | NumberField),
                                        unit: e.target.value || undefined,
                                    })
                                }
                                placeholder="m², €, jours..."
                            />
                        </label>
                    )}

                    {field.type === 'textarea' && (
                        <>
                            <label className="field">
                                <span className="field-label">Placeholder</span>
                                <input
                                    type="text"
                                    value={(field as TextareaField).placeholder ?? ''}
                                    onChange={(e) =>
                                        onChange({
                                            ...(field as TextareaField),
                                            placeholder: e.target.value || undefined,
                                        })
                                    }
                                    placeholder="Décrivez votre projet..."
                                />
                            </label>
                            <label className="field">
                                <span className="field-label">Longueur max (optionnel)</span>
                                <input
                                    type="number"
                                    value={(field as TextareaField).maxLength ?? ''}
                                    onChange={(e) =>
                                        onChange({
                                            ...(field as TextareaField),
                                            maxLength: e.target.value
                                                ? Number(e.target.value)
                                                : undefined,
                                        })
                                    }
                                    placeholder="500"
                                />
                            </label>
                        </>
                    )}

                    {fieldHasOptions(field) && (
                        <OptionsEditor
                            options={field.options}
                            withIcon={field.type === 'chips'}
                            onChange={setOptions}
                        />
                    )}

                    <CollapsibleBlock
                        title="Affichage conditionnel"
                        icon={<Icon name="branch" size={15} />}
                        hasValue={!!field.visibility}
                    >
                        <VisibilityControl
                            config={config}
                            rule={field.visibility}
                            fieldId={field.id}
                            onChange={(rule) => {
                                if (rule === undefined) {
                                    const { visibility: _v, ...rest } = field as Field & { visibility?: VisibilityRule };
                                    onChange(rest as Field);
                                } else {
                                    onChange({ ...field, visibility: rule } as Field);
                                }
                            }}
                        />
                    </CollapsibleBlock>

                    {field.type !== 'textarea' && (
                        <CollapsibleBlock
                            title="Impact sur le prix"
                            icon={<Icon name="price" size={15} />}
                            hasValue={!!field.pricing && field.pricing.type !== 'none'}
                        >
                            <ImpactEditor
                                context="field"
                                field={field}
                                impact={field.pricing}
                                onChange={(next) => {
                                    if (!next || next.type === 'none') {
                                        const { pricing: _p, ...rest } = field as Field & { pricing?: PricingImpact };
                                        onChange(rest as Field);
                                    } else {
                                        onChange({ ...field, pricing: next } as Field);
                                    }
                                }}
                            />
                        </CollapsibleBlock>
                    )}
                </div>
            )}
        </li>
    );
}

/* ---------- Sous-composants ---------- */

interface VisibilityControlProps {
    config: GeneratorConfigV1;
    rule: VisibilityRule | undefined;
    fieldId: string;
    onChange: (next: VisibilityRule | undefined) => void;
}

function VisibilityControl({ config, rule, fieldId, onChange }: VisibilityControlProps) {
    return (
        <>
            <label className="field field-checkbox">
                <input
                    type="checkbox"
                    checked={!!rule}
                    onChange={(e) => {
                        if (e.target.checked) {
                            onChange({ fieldId: '' });
                        } else {
                            onChange(undefined);
                        }
                    }}
                />
                <span>N'afficher ce champ que si une condition est remplie</span>
            </label>
            {rule && (
                <ConditionBuilder
                    config={config}
                    rule={rule}
                    onChange={(next) => onChange(next)}
                    excludeFieldId={fieldId}
                />
            )}
        </>
    );
}
